import { useParams, useNavigate } from "react-router-dom";
import { useCampaign } from "../hooks/use-campaigns";
import {
  getCampaignStatus,
  getParticipationLimitText,
  getParticipationLimitDescription,
  getAvailableParticipationCountText,
} from "../utils/campaign-utils";
import { SkeletonWave } from "../../../components/common/skeleton-wave";
import { useState, useEffect, useMemo, useRef } from "react";
import {
  FaChevronLeft,
  FaShoppingBag,
  FaUtensils,
  FaBox,
  FaCamera,
  FaImage,
  FaUpload,
  FaCheck,
  FaTimes,
  FaHeart,
  // FaExclamationCircle
} from "react-icons/fa";
import { IoMdInformationCircleOutline } from "react-icons/io";
import CustomMapMarker from "../../../components/common/custom-map-marker";
import clsx from "clsx";
import instagramIcon from "../../store/assets/instagram.jpeg";
import naverIcon from "../../store/assets/naver.png";
import mahiLogo from "../../../assets/images/mahi-download-logo-512.png";
import ParticipationGuideSheet from "../components/participation-guide-sheet";
import { useParticipationGuide } from "../../../hooks/use-participation-guide";
import DraggableBottomSheet from "../../../components/common/draggable-bottom-sheet";
import { useMutation, useQuery } from "@apollo/client";
import {
  LIKE_BRAND,
  CANCEL_BRAND_LIKE,
  GET_USER_BRAND_LIKES,
} from "../api/brand-like";
import { GET_CAMPAIGN } from "../api/campaigns";
import { notify, notifyError } from "../../../utils/toast";

const CampaignDetailPage = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const campaignIdNum = campaignId ? parseInt(campaignId) : 0;
  const { campaign, loading } = useCampaign(campaignIdNum);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [isMapToggleOpen, setIsMapToggleOpen] = useState(false);
  const [isPhotoWarningOpen, setIsPhotoWarningOpen] = useState(false);
  const mapElement = useRef<HTMLDivElement | null>(null);
  const { shouldShow, markAsShown } = useParticipationGuide();

  // 브랜드 좋아요 상태 확인
  const { data: brandLikesData } = useQuery(GET_USER_BRAND_LIKES, {
    skip: !campaign?.brand?.id,
  });

  const isBrandLiked = useMemo(() => {
    if (!campaign?.brand?.id || !brandLikesData?.getUserBrandLikes) {
      return false;
    }
    return brandLikesData.getUserBrandLikes.some(
      (like: any) => like.brandId === campaign.brand.id,
    );
  }, [campaign?.brand?.id, brandLikesData]);

  // 브랜드 좋아요 뮤테이션
  const [likeBrand] = useMutation(LIKE_BRAND, {
    refetchQueries: [
      { query: GET_CAMPAIGN, variables: { campaignId: campaignIdNum } },
      { query: GET_USER_BRAND_LIKES },
    ],
    onCompleted: () => {
      notify("이 브랜드의 새 캠페인 정보를 받아볼 수 있어요");
    },
    onError: (error) => {
      notifyError("좋아요 처리 중 오류가 발생했어요");
      console.error("좋아요 오류:", error);
    },
  });

  const [cancelBrandLike] = useMutation(CANCEL_BRAND_LIKE, {
    refetchQueries: [
      { query: GET_CAMPAIGN, variables: { campaignId: campaignIdNum } },
      { query: GET_USER_BRAND_LIKES },
    ],
    onCompleted: () => {
      notify("브랜드 좋아요를 취소했어요");
    },
    onError: (error) => {
      notifyError("좋아요 취소 중 오류가 발생했어요");
      console.error("좋아요 취소 오류:", error);
    },
  });

  const handleBrandLikeClick = async () => {
    if (!campaign?.brand?.id) return;

    try {
      if (isBrandLiked) {
        await cancelBrandLike({
          variables: { brandId: campaign.brand.id },
        });
      } else {
        await likeBrand({
          variables: { brandId: campaign.brand.id },
        });
      }
    } catch (error) {
      // 에러는 onError에서 처리됨
    }
  };

  // 미래의 회차만 필터링 (날짜가 지나지 않은 것만)
  const availableRuns = useMemo(() => {
    return (
      campaign?.runs?.filter((run: any) => {
        const now = new Date();
        const runEndAt = new Date(run.endAt);
        return run.isActive && runEndAt > now;
      }) || []
    );
  }, [campaign?.runs]);

  // 선택된 회차 또는 첫 번째 회차
  const selectedRun =
    availableRuns.find((run: any) => run.id === selectedRunId) ||
    availableRuns[0] ||
    null;

  // 사진 인증(proofType === "PHOTO")이 있는지 확인
  const hasPhotoProofType = useMemo(() => {
    return (
      campaign?.steps?.some((step: any) => step.proofType === "PHOTO") || false
    );
  }, [campaign?.steps]);

  // 사진 인증 step의 stepOrder 찾기
  const photoStepOrder = useMemo(() => {
    const photoStep = campaign?.steps?.find(
      (step: any) => step.proofType === "PHOTO",
    );
    return photoStep?.stepOrder || null;
  }, [campaign?.steps]);

  // 초기 선택 설정
  useEffect(() => {
    if (availableRuns.length > 0 && !selectedRunId) {
      setSelectedRunId(availableRuns[0].id);
    }
  }, [availableRuns, selectedRunId]);

  // 지도 초기화
  useEffect(() => {
    if (
      !mapElement.current ||
      !campaign?.brand?.lat ||
      !campaign?.brand?.lng ||
      !isMapToggleOpen
    ) {
      return;
    }

    const { naver } = window;
    if (!naver) return;

    const AddressY = Number(campaign.brand.lat);
    const AddressX = Number(campaign.brand.lng);
    const center = new naver.maps.LatLng(AddressY, AddressX);
    const mapOptions: naver.maps.MapOptions = {
      center,
      zoom: 16,
      minZoom: 7,
      maxZoom: 19,
      zoomControl: false,
      mapDataControl: false,
      scaleControl: false,
    };

    const map = new naver.maps.Map(mapElement.current, mapOptions);

    try {
      const newMarker = new naver.maps.Marker({
        position: new naver.maps.LatLng(AddressY, AddressX),
        map,
        title: campaign.brand.name || "",
        clickable: true,
        icon: {
          content: CustomMapMarker(),
          size: new naver.maps.Size(20, 20),
        },
      });
      newMarker.setTitle(campaign.brand.name || "");
    } catch (e) {
      console.error("Marker creation error:", e);
    }
  }, [campaign, isMapToggleOpen]);

  const faqItems = [
    {
      question: "페이백 미션이란?",
      answer:
        "페이백 미션은 제품을 구매하고 미션을 완료하면 일부 금액을 돌려받는 서비스입니다. 예치금을 걸고 미션을 성공적으로 완료하면 예치금과 페이백을 모두 받을 수 있습니다.",
    },
    {
      question: "왜 이런 미션을 하나요?",
      answer:
        "마감히어로는 우리 동네 식품 득템 앱으로, 여러분이 돈 걱정 없이 마음껏 음식을 즐길 수 있는 기회를 제공하고자 페이백 미션을 진행하고 있어요. 이혜택을 통해 더 많은 사람들이 마감히어로에서 음식 득템의 즐거움을 느끼고 다향한 제품을 부담없이 경험할 수 있길 바랍니다. 이 기회를 놓치지 말고 꼭 참여해보세요!",
    },
    {
      question: "페이백은 언제 받나요?",
      answer:
        "정해진 인증을 완료하시면, 영업일 기준 최소 1일 ~ 최대 3일 이내 페이백을 받으실 수 있습니다.",
    },
    {
      question: "왜 예치금을 걸어야 하나요?",
      answer:
        "예치금은 미션 인증을 진행하겠다는 약속의 의미로 걸어두는 돈입니다. 인증을 성공적으로 완료하면 페이백과 함께 예치금도 전액 환급됩니다. 예치금은 인증을 모두 성공하면 돌려받을 수 있는 돈이니 부담 없이 참가하세요. :)",
    },
    {
      question: "인증을 실패하면 어떻게 되나요?",
      answer:
        "인증 사진이 기준에 맞지 않아 실패할 경우, 재인증이 가능합니다. 재인증 가이드는 카카오톡 알림톡 또는 문자로 안내해드려요. 미션 기간내 재인증 후 성공하시면 페이백과 예치금이 정상적으로 모두 지급된니다.",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <SkeletonWave className="pt-8" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            캠페인을 찾을 수 없습니다
          </h1>
          <button
            onClick={() => navigate("/campaigns")}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const status = selectedRun
    ? getCampaignStatus(selectedRun.startAt, selectedRun.endAt)
    : "ended";

  const discountRate = campaign.discountRate
    ? (parseFloat(campaign.discountRate.toString()) * 100).toFixed(0)
    : null;

  const handleBackClick = () => {
    const idx = (window.history.state && window.history.state?.idx) ?? 0;
    idx > 0 ? navigate(-1) : navigate("/campaigns");
  };

  // Helper to format date range for "Purchase Time" with AM/PM
  const getFormattedRunTime = (run: any) => {
    if (!run) return "";
    const startDate = new Date(run.startAt);
    const endDate = new Date(run.endAt);

    const getMonthDay = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
    const getAmPmHour = (d: Date) => {
      const h = d.getHours();
      const ampm = h < 12 ? "오전" : "오후";
      const hour = h % 12 || 12; // 0시는 12시로 표시
      return `${ampm} ${hour}시`;
    };

    // 시작일과 종료일이 모두 00시인 경우
    const isStartMidnight =
      startDate.getHours() === 0 && startDate.getMinutes() === 0;
    const isEndMidnight =
      endDate.getHours() === 0 && endDate.getMinutes() === 0;
    const timeDiff = endDate.getTime() - startDate.getTime();
    const oneDayInMs = 86400000; // 24시간

    if (isStartMidnight && isEndMidnight) {
      // 정확히 24시간 (1일)인 경우: "1/16일 하루동안"
      if (timeDiff === oneDayInMs) {
        return `${getMonthDay(startDate)}일 하루동안`;
      }
      // 1일 이상 차이인 경우: 종료일을 하루 빼서 표기 (예: 1/16 00시 ~ 1/18 00시 → 1/16~1/17)
      const displayEndDate = new Date(endDate);
      displayEndDate.setDate(displayEndDate.getDate() - 1);
      return `${getMonthDay(startDate)}~${getMonthDay(displayEndDate)}`;
    }

    const isSameDay =
      startDate.getDate() === endDate.getDate() &&
      startDate.getMonth() === endDate.getMonth();

    const startStr = `${getMonthDay(startDate)} ${getAmPmHour(startDate)}`;

    if (isSameDay) {
      return `${startStr} ~ ${getAmPmHour(endDate)}`;
    } else {
      return `${startStr} ~ ${getMonthDay(endDate)} ${getAmPmHour(endDate)}`;
    }
  };

  return (
    <div className="min-h-screen bg-white pb-32 font-sans">
      {/* Header Image & Nav */}
      <div className="relative w-full aspect-square bg-gray-100">
        <button
          onClick={handleBackClick}
          className="absolute top-4 left-4 z-10 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center shadow-sm"
        >
          <FaChevronLeft className="text-gray-800 text-lg" />
        </button>

        {campaign.productImageUrl ? (
          <img
            src={campaign.productImageUrl}
            alt={campaign.productName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            이미지 없음
          </div>
        )}
      </div>

      {/* Title & Price Section */}
      <div className="px-5 py-6">
        <div className="inline-block px-2 py-1 border border-blue-400 text-blue-500 text-xs font-bold rounded mb-2">
          {campaign.channel === "NAVER"
            ? "네이버"
            : campaign.channel === "COUPANG"
              ? "쿠팡"
              : campaign.channel === "CARROT"
                ? "당근"
                : campaign.channel}
        </div>
        <h1 className="text-xl font-bold text-gray-900 leading-snug mb-6">
          {campaign.productName} 구매하기
        </h1>

        {/* Price Info Grid */}
        <div className="space-y-3">
          <div className="flex justify-between items-center text-gray-500">
            <span className="text-sm">구매가</span>
            <span className="text-base font-medium line-through decoration-gray-400">
              {campaign.originalPrice.toLocaleString()}원
            </span>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <span className="text-gray-500 text-sm">페이백</span>
              <IoMdInformationCircleOutline className="text-gray-400" />
            </div>
            <div className="flex items-center gap-2">
              {discountRate && (
                <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {discountRate}%
                </span>
              )}
              <span className="text-blue-600 font-bold text-lg">
                {Math.floor(campaign.paybackPrice).toLocaleString()}원
              </span>
            </div>
          </div>
          <div className="flex justify-between items-center pt-2">
            <div className="flex items-center gap-1">
              <span className="text-gray-900 font-bold">실구매가</span>
              <IoMdInformationCircleOutline className="text-gray-400" />
            </div>
            <span className="text-blue-600 font-bold text-2xl">
              {campaign.discountPrice.toLocaleString()}원
            </span>
          </div>
        </div>

        {/* Purchase Time Display (Single) */}
        {availableRuns.length === 1 && (
          <div className="bg-gray-50 rounded-xl p-4 flex justify-between items-center mt-6">
            <span className="font-bold text-gray-800">구매 시간</span>
            <span className="font-bold text-gray-900">
              {selectedRun
                ? getFormattedRunTime(selectedRun)
                : "기간 확인 필요"}
            </span>
          </div>
        )}
      </div>

      <div className="h-2 bg-gray-100" />

      {/* Purchase Time Selection Section (Multiple) */}
      {availableRuns.length > 1 && (
        <div className="px-5 py-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            구매 시간 선택
          </h2>
          <div className="space-y-4">
            {availableRuns.map((run: any) => {
              const isSelected = selectedRunId === run.id;
              return (
                <label
                  key={run.id}
                  className="flex items-start gap-3 cursor-pointer group"
                >
                  <div className="relative flex items-center justify-center w-6 h-6 mt-0.5">
                    <input
                      type="radio"
                      name="run_select"
                      checked={isSelected}
                      onChange={() => setSelectedRunId(run.id)}
                      className="peer appearance-none w-6 h-6 border-2 border-gray-300 rounded-full checked:border-blue-600 checked:bg-white transition-colors"
                    />
                    <div className="absolute w-3 h-3 bg-blue-600 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity" />
                  </div>
                  <div className="flex-1">
                    <div
                      className={`text-base font-bold mb-1 ${
                        isSelected ? "text-gray-900" : "text-gray-900"
                      }`}
                    >
                      {getFormattedRunTime(run)}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 font-bold rounded-sm">
                        페이백{" "}
                        {Math.floor(campaign.paybackPrice).toLocaleString()}원
                      </span>
                      <span className="text-gray-900 font-bold text-sm">
                        {campaign.discountPrice.toLocaleString()}원
                      </span>
                      <span className="text-gray-400 text-sm line-through decoration-gray-400">
                        {campaign.originalPrice.toLocaleString()}원
                      </span>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {availableRuns.length > 1 && <div className="h-2 bg-gray-100" />}

      {/* 매장정보 섹션 */}
      {campaign?.brand?.lat && campaign?.brand?.lng && (
        <>
          <div className="h-2 bg-gray-100" />
          <div className="px-5 py-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">매장정보</h2>
              {campaign.brand.id && (
                <button
                  onClick={handleBrandLikeClick}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors"
                  style={{
                    backgroundColor: isBrandLiked ? "#fee2e2" : "#f3f4f6",
                    color: isBrandLiked ? "#dc2626" : "#6b7280",
                  }}
                >
                  <FaHeart
                    className={clsx("text-base", {
                      "fill-current": isBrandLiked,
                    })}
                  />
                  <span className="text-sm font-semibold">
                    {isBrandLiked ? "좋아요" : "좋아요"}
                  </span>
                </button>
              )}
            </div>
            <div className="space-y-2 mb-4">
              {campaign.brand.name && (
                <div className="flex items-start">
                  <span className="text-gray-600 text-sm font-semibold min-w-[80px]">
                    매장명
                  </span>
                  <span className="text-gray-900 text-sm">
                    {campaign.brand.name}
                  </span>
                </div>
              )}
              {campaign.brand.roadAddr && (
                <div className="flex items-start">
                  <span className="text-gray-600 text-sm font-semibold min-w-[80px]">
                    매장주소
                  </span>
                  <span className="text-gray-900 text-sm">
                    {campaign.brand.roadAddr}
                  </span>
                </div>
              )}
            </div>

            {/* 참여 제한 정보 */}
            {campaign.contract &&
              getParticipationLimitText(campaign.contract) && (
                <>
                  <div className="border-t border-gray-200 my-4"></div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <span className="text-gray-600 text-sm font-semibold min-w-[80px]">
                          미션 참여횟수
                        </span>
                        <div className="flex-1 space-y-1">
                          <div className="text-gray-900 text-sm whitespace-pre-line">
                            {campaign.contract?.monthlyParticipationLimit ? (
                              <>
                                매월{" "}
                                <span className="font-bold">
                                  {campaign.contract.monthlyParticipationLimit}
                                  회
                                </span>
                              </>
                            ) : (
                              getParticipationLimitDescription(
                                campaign.contract,
                              )
                            )}
                          </div>
                          {campaign.contract?.monthlyParticipationLimit && (
                            <div className="text-gray-400 text-xs">
                              (ex.{" "}
                              {(() => {
                                const now = new Date();
                                const months = [];
                                for (let i = 0; i < 3; i++) {
                                  const date = new Date(
                                    now.getFullYear(),
                                    now.getMonth() + i,
                                    1,
                                  );
                                  const month = date.getMonth() + 1;
                                  months.push(
                                    `${month}월 ${campaign.contract.monthlyParticipationLimit}회`,
                                  );
                                }
                                return months.join(", ");
                              })()}
                              )
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

            {/* 먹는 방식 안내 박스 */}
            {campaign.eatingMethods && campaign.eatingMethods.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3">
                  {campaign.eatingMethods.includes("포장") &&
                  campaign.eatingMethods.includes("매장식사") ? (
                    <>
                      <div className="flex items-center gap-1.5">
                        <FaBox className="text-orange-500 text-lg flex-shrink-0" />
                        <FaUtensils className="text-orange-500 text-lg flex-shrink-0" />
                      </div>
                      <span className="text-gray-900 font-semibold text-sm">
                        포장과 매장식사 모두 가능한 미션입니다.
                      </span>
                    </>
                  ) : campaign.eatingMethods.includes("포장") ? (
                    <>
                      <FaBox className="text-orange-500 text-lg flex-shrink-0" />
                      <span className="text-gray-900 font-semibold text-sm">
                        포장만 가능한 미션입니다!
                      </span>
                    </>
                  ) : (
                    <>
                      <FaUtensils className="text-orange-500 text-lg flex-shrink-0" />
                      <span className="text-gray-900 font-semibold text-sm">
                        매장식사만 가능한 미션입니다.
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* 버튼들: 지도보기 → 네이버 길찾기 → 인스타 */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setIsMapToggleOpen(!isMapToggleOpen)}
                className="px-3 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
              >
                {isMapToggleOpen ? "🗺️ 지도닫기" : "🗺️ 지도보기"}
              </button>
              {campaign.brand.naverPlaceUrl && (
                <a
                  href={campaign.brand.naverPlaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-3 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  <img
                    src={naverIcon}
                    alt="네이버 길찾기"
                    className="w-4 h-4 rounded mr-2"
                  />
                  <span>네이버 길찾기</span>
                </a>
              )}
              {campaign.brand.instagramUrl && (
                <a
                  href={campaign.brand.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-3 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  <img
                    src={instagramIcon}
                    alt="인스타그램"
                    className="w-4 h-4 rounded mr-2"
                  />
                  <span>인스타</span>
                </a>
              )}
            </div>

            <div
              className={clsx("border-t border-gray-100 pt-4", {
                hidden: !isMapToggleOpen,
              })}
            >
              <div
                ref={mapElement}
                id="campaign-map"
                style={{
                  width: "100%",
                  height: "180px",
                  borderRadius: 12,
                  border: "1px solid #e0e0e0",
                }}
                className={clsx({ hidden: !isMapToggleOpen })}
              ></div>
            </div>
          </div>
        </>
      )}

      <div className="h-2 bg-gray-100" />

      {/* Payback Method & Steps & Reward Combined Section */}
      <div className="px-5 py-8">
        <h2 className="text-lg font-bold text-gray-900 mb-6">
          페이백 받는 방법
        </h2>

        <div className="relative">
          {/* Continuous Vertical Line - Centered at 14px (half of w-7 bullet) */}
          <div className="absolute left-[13.5px] top-4 bottom-16 w-[1px] bg-gray-100 -z-0" />

          <div className="space-y-12">
            {/* 1. Summary Step 1: Participate */}
            <div className="flex gap-4 items-start">
              <div className="w-7 h-7 bg-white border border-gray-100 shadow-sm rounded-full flex items-center justify-center flex-shrink-0 z-10">
                <span className="text-sm">👉</span>
              </div>
              <div className="pt-1">
                <p className="font-bold text-gray-800">
                  먼저, 미션에 참가해요!
                </p>
              </div>
            </div>

            {/* 2. Summary Step 2: Purchase */}
            <div className="flex gap-4 items-start">
              <div className="w-7 h-7 bg-gray-600 rounded-full flex items-center justify-center text-white shadow-sm flex-shrink-0 z-10">
                <FaShoppingBag className="text-xs" />
              </div>
              <div className="flex-1 pt-1">
                <p className="font-bold text-gray-800 mb-1">
                  {selectedRun ? getFormattedRunTime(selectedRun) : ""}에
                </p>
                <p className="text-blue-600 font-bold mb-3">
                  {campaign.channel === "NAVER"
                    ? "네이버"
                    : campaign.channel === "COUPANG"
                      ? "쿠팡"
                      : campaign.channel === "CARROT"
                        ? "당근"
                        : campaign.channel}
                  <span className="text-gray-800 font-normal">
                    에서 제품을 구매해요.
                  </span>
                </p>
                {campaign.productUrl && (
                  <a
                    href={campaign.productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center border border-blue-500 text-blue-600 font-bold py-3 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    바로가기
                  </a>
                )}
              </div>
            </div>

            {/* 3. Detailed Steps (Dynamic) */}
            {campaign.steps?.map((step: any, index: number) => (
              <div key={step.id || index} className="flex gap-4 items-start">
                <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 z-10">
                  {step.stepOrder}
                </div>

                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    {step.title || `미션 ${step.stepOrder}`}
                  </h3>
                  {/* Guide Text */}
                  {step.guide && Array.isArray(step.guide) && (
                    <div className="space-y-1 text-gray-600 text-md mb-6">
                      {step.guide.map((guide: string, idx: number) => (
                        <p key={idx}>{guide}</p>
                      ))}
                    </div>
                  )}

                  {/* Requirements */}
                  {step.requirements &&
                    Array.isArray(step.requirements) &&
                    step.requirements.length > 0 && (
                      <div className="bg-gray-50 p-3 rounded-lg mb-4">
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                          {step.requirements.map((req: string, idx: number) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {/* Image */}
                  <div className="relative w-full aspect-[3/4] sm:aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                    {step.imageUrl ? (
                      <img
                        src={step.imageUrl}
                        alt={step.title}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-2">
                        <span className="font-bold">인증샷 예시</span>
                        <span className="text-xs">
                          ({step.title || `단계 ${step.stepOrder}`} 캡처)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* 4. Payback Reward Card */}
            <div>
              <div className="flex gap-4 items-center mb-2">
                <div className="w-7 h-7 bg-white flex items-center justify-center flex-shrink-0 z-10 text-xl">
                  🎉
                </div>
                <h2 className="text-xl font-bold text-gray-800">
                  {Math.floor(campaign.paybackPrice).toLocaleString()}원 페이백!
                </h2>
              </div>
              <div className="pl-[44px]">
                <p className="text-gray-500 text-md mb-6">
                  모든 미션 검토가 끝나면 1-3일 내 페이백이 진행돼요!!
                </p>

                {/* Ticket Style Card */}
                <div className="bg-gray-50 p-6 rounded-lg flex items-center justify-center">
                  <div className="relative bg-gradient-to-r from-blue-500 to-indigo-600 w-full max-w-sm h-32 rounded-2xl shadow-lg flex flex-col items-center justify-center text-white overflow-hidden">
                    {/* Cutout circles */}
                    <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-gray-50 rounded-full" />
                    <div className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-gray-50 rounded-full" />

                    <span className="font-medium mb-1 opacity-90 text-sm">
                      페이백
                    </span>
                    <span className="text-3xl font-bold">
                      {Math.floor(campaign.paybackPrice).toLocaleString()}원
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-2 bg-gray-100" />

      {/* FAQ */}
      <div className="bg-white px-5 py-8 border-t border-gray-100">
        <h2 className="text-xl font-bold mb-4">자주 묻는 질문</h2>
        <div className="space-y-2">
          {faqItems.map((faq, index) => (
            <div key={index} className="border-b border-gray-200 last:border-0">
              <button
                onClick={() =>
                  setOpenFaqIndex(openFaqIndex === index ? null : index)
                }
                className="w-full text-left py-4 flex items-center justify-between hover:bg-gray-50 px-2 rounded"
              >
                <span className="font-medium">{faq.question}</span>
                <span className="text-gray-400">
                  {openFaqIndex === index ? "▲" : "▼"}
                </span>
              </button>
              {openFaqIndex === index && (
                <div className="px-2 pb-4 text-gray-700 text-sm">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Notes (유의사항) */}
      <div className="px-5 py-8 bg-gray-50">
        <h2 className="text-lg font-bold mb-4 text-gray-800">유의사항</h2>
        <div className="text-sm text-gray-500 space-y-2">
          <p>• 미션 시작 1일 전부터는 취소가 불가합니다.</p>
          <p>• 구매 시간 및 인증 시간은 한국 시간 기준입니다.</p>
          <p>• 도용 및 중복으로 판단되면 미인증 처리돼요.</p>
          <p>
            • 제품 구매/배송/환불 등은{" "}
            <span className="font-medium">
              {campaign.channel === "NAVER"
                ? "네이버"
                : campaign.channel === "COUPANG"
                  ? "쿠팡"
                  : campaign.channel === "CARROT"
                    ? "당근"
                    : campaign.channel}
            </span>
            에 문의해주세요.
          </p>
          <p>
            • 구매일에 제품 가격이 변동될 경우 페이백 금액도 함께 변동되므로
            모집 기간에 확인하신 실 구매가는 높아지지 않습니다.
          </p>
          <p>
            • 재판매 등 부정한 목적 및 영리 추구를 위한 미션 참여가 확인될 경우
            서비스 이용 정지 및 손해배상 청구가 진행될 수 있어요.{" "}
            <span className="text-blue-600 underline cursor-pointer">
              서비스 이용 약관 참고
            </span>
          </p>
          <p>
            • 마감히어로에서 인증샷을 전수 검사하며, 인증샷 도용과 조작 및
            규정과 무관한 인증샷을 올리실 경우 미션 참여가 예고 없이 취소되며
            이후 미션 참여가 제한될 수 있어요.
          </p>
          <p>
            • 리뷰 작성 후 취소/반품이 확인될 경우 포인트 회수 및 블랙리스트
            처리되어 이후 미션에 참여하실 수 없어요.
          </p>
          <p>
            • 제휴 미션 모집 기간 종료 후 취소 요청을 1달 내 3회 이상 하시는
            분들은 추후 제휴 미션 참여에 제한이 있어요.
          </p>
          <p>
            • 앱(접속) 오류 등과 같은 사유로 기간 내 인증이 어려운 경우 인증패스
            처리되며, 예치금, 반환 및 페이백 지급이 이루어지지 않아요. 이 경우
            건강한 미션 경험을 제공하기 위해 오류없는 기기 사용을 권장해요.
          </p>
          <p>
            • 페이백 수령 시 작성하는 응답을 3회 이상 무성의하게 작성하시는 경우
            추후 제휴 미션 참여에 제한이 있어요.
          </p>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 safe-area-pb z-50">
        {/* 참여 가능 횟수 안내 (참가하기 버튼 위) */}
        {campaign.contract &&
          getAvailableParticipationCountText(campaign.contract) && (
            <div className="text-center mb-3">
              <span
                className={`text-sm font-bold ${
                  campaign.contract.remainingParticipationCount !== null &&
                  campaign.contract.remainingParticipationCount !== undefined &&
                  campaign.contract.remainingParticipationCount > 0
                    ? "text-green-600"
                    : "text-gray-500"
                }`}
              >
                {getAvailableParticipationCountText(campaign.contract)}
              </span>
            </div>
          )}

        <div className="flex gap-2">
          {/* 제품 바로가기 버튼 */}
          {campaign.productUrl ? (
            <a
              href={campaign.productUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-1/2 py-4 bg-white border-2 border-blue-600 text-blue-600 font-bold text-lg rounded-xl hover:bg-blue-50 transition-colors text-center"
            >
              제품 바로가기
            </a>
          ) : (
            <div className="w-1/2" />
          )}

          {/* 참가하기 버튼 */}
          <button
            onClick={() => {
              if (!selectedRun?.isFull && selectedRun) {
                // 사진 인증이 있는 경우 바텀시트 표시
                if (hasPhotoProofType) {
                  setIsPhotoWarningOpen(true);
                } else {
                  navigate(
                    `/campaigns/${campaign.id}/join?runId=${selectedRun.id}`,
                  );
                }
              }
            }}
            disabled={
              !selectedRun ||
              selectedRun?.isFull ||
              status === "ended" ||
              (campaign.contract?.remainingParticipationCount !== null &&
                campaign.contract?.remainingParticipationCount !== undefined &&
                campaign.contract.remainingParticipationCount <= 0)
            }
            className={`w-1/2 py-4 rounded-xl font-bold text-lg text-white transition-colors ${
              !selectedRun ||
              selectedRun?.isFull ||
              status === "ended" ||
              (campaign.contract?.remainingParticipationCount !== null &&
                campaign.contract?.remainingParticipationCount !== undefined &&
                campaign.contract.remainingParticipationCount <= 0)
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {status === "ended"
              ? "종료된 캠페인"
              : selectedRun?.isFull
                ? "마감되었습니다"
                : campaign.contract?.remainingParticipationCount !== null &&
                    campaign.contract?.remainingParticipationCount !==
                      undefined &&
                    campaign.contract.remainingParticipationCount <= 0
                  ? "참여 횟수 초과"
                  : "미션 참가하기"}
          </button>
        </div>
      </div>

      {/* 참여방법 시트 */}
      <ParticipationGuideSheet isOpen={shouldShow} onClose={markAsShown} />

      {/* 사진 인증 안내 바텀시트 */}
      <DraggableBottomSheet
        isOpen={isPhotoWarningOpen}
        onClose={() => setIsPhotoWarningOpen(false)}
        showOverlay={true}
      >
        <div className="w-full -mt-4">
          {/* Header */}
          <div className="pt-2 pb-3 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-1.5">
              {photoStepOrder
                ? `${photoStepOrder}번 식사 인증 안내`
                : "식사 인증 안내"}
            </h2>
            <p className="text-xs text-gray-600 leading-relaxed">
              부정행위 방지를 위해, 인증은 마감히어로 앱에서 촬영한 사진만
              가능합니다.
            </p>
          </div>

          {/* Main Content - Split Comparison */}
          <div className="py-4 relative">
            <div className="grid grid-cols-2 gap-4">
              {/* LEFT SIDE - Invalid */}
              <div className="relative flex flex-col items-center text-center p-3">
                {/* Background */}
                <div className="absolute inset-0 bg-gray-50 border border-gray-200 rounded-lg -z-10"></div>
                {/* Label with X icon */}
                <div className="flex items-center gap-1 mb-2">
                  <div className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center">
                    <FaTimes className="w-2.5 h-2.5 text-red-600" />
                  </div>
                  <span className="text-xs font-semibold text-red-600">
                    인증 불가
                  </span>
                </div>

                {/* Illustration Flow */}
                <div className="space-y-2 mb-3 w-full">
                  {/* Default Camera Icon */}
                  <div className="flex justify-center">
                    <div className="w-10 h-10 rounded-xl bg-gray-200 flex items-center justify-center opacity-50">
                      <FaCamera className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center">
                    <div className="w-px h-3 bg-gray-300"></div>
                  </div>

                  {/* Gallery Icon */}
                  <div className="flex justify-center">
                    <div className="w-10 h-10 rounded-xl bg-gray-200 flex items-center justify-center opacity-50">
                      <FaImage className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center">
                    <div className="w-px h-3 bg-gray-300"></div>
                  </div>

                  {/* Upload Icon */}
                  <div className="flex justify-center">
                    <div className="w-10 h-10 rounded-xl bg-gray-200 flex items-center justify-center opacity-50">
                      <FaUpload className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                </div>

                {/* Caption */}
                <div className="text-[10px] text-gray-500 leading-relaxed">
                  <p>기본 카메라 /</p>
                  <p>갤러리 사진 업로드</p>
                  <p className="font-semibold text-red-600 mt-1">(인증 불가)</p>
                </div>
              </div>

              {/* RIGHT SIDE - Valid */}
              <div className="relative flex flex-col items-center text-center p-3">
                {/* Background */}
                <div className="absolute inset-0 bg-green-50 border border-green-200 rounded-lg -z-10"></div>
                {/* Label with Check icon */}
                <div className="flex items-center gap-1 mb-2">
                  <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center">
                    <FaCheck className="w-2.5 h-2.5 text-blue-600" />
                  </div>
                  <span className="text-xs font-semibold text-blue-600">
                    인증 가능
                  </span>
                </div>

                {/* Illustration Flow */}
                <div className="space-y-2 mb-3 w-full">
                  {/* MagamHero App Logo */}
                  <div className="flex justify-center">
                    <div className="w-10 h-10 rounded-xl overflow-hidden shadow-sm">
                      <img
                        src={mahiLogo}
                        alt="마감히어로"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center">
                    <div className="w-px h-3 bg-blue-300"></div>
                  </div>

                  {/* "인증하기" Button */}
                  <div className="flex justify-center">
                    <div className="px-3 my-2 bg-blue-600 text-white text-[10px] font-semibold rounded-lg h-5 flex items-center justify-center">
                      인증하기
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center">
                    <div className="w-px h-3 bg-blue-300"></div>
                  </div>

                  {/* In-app Camera */}
                  <div className="flex justify-center">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center shadow-lg">
                      <FaCamera className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>

                {/* Caption */}
                <div className="text-[10px] text-gray-700 leading-relaxed">
                  <p>마감히어로 앱에서</p>
                  <p>촬영 후 바로 제출</p>
                  <p className="font-semibold text-blue-600 mt-1">
                    (인증 가능)
                  </p>
                </div>
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2"></div>
          </div>

          {/* Footer CTA - 커스텀 스타일 */}
          <div className="border-t border-gray-100 pt-4">
            <button
              className="w-full bg-blue-600 text-white font-semibold py-4 rounded-xl shadow-lg hover:bg-blue-700 transition-colors active:scale-[0.98]"
              onClick={() => {
                setIsPhotoWarningOpen(false);
                if (selectedRun && !selectedRun.isFull) {
                  navigate(
                    `/campaigns/${campaign.id}/join?runId=${selectedRun.id}`,
                  );
                }
              }}
            >
              확인
            </button>
          </div>
        </div>
      </DraggableBottomSheet>
    </div>
  );
};

export default CampaignDetailPage;
